import React, { useEffect, useState } from "react";
import {
    Box,
    Button,
    Card,
    Container,
    Stack,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    Typography,
    Select,
    MenuItem,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";

import { Page } from "../../../../components";
import { TeamForm } from "../../../../components/groups-and-teams/team-form";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { TagsService } from "../../../../services/tags-service";
import { useSelector } from "react-redux";
import { RootState, store } from "../../../../store";
import { TeamService } from "../../../../services/team.service";
import { toast } from "react-toastify";
import { permissionCheck } from "../../../../helpers/permissionCheck";
import { customerPermissionFlags } from "../../../../constants/systemPermissions.constants";

export const TeamFormPage: React.FC = () => {
    const navigate = useNavigate();
    const { teamId } = useParams<{ teamId: string }>();
    const [confirmOpen, setConfirmOpen] = useState(false);
    const { selectedTeam, items: allTeams } = useSelector((state: RootState) => state.teams);
    const { selectedGroup } = useSelector((state: RootState) => state.groups);
    
    const isEditMode = !!teamId;



const [groupModalOpen, setGroupModalOpen] = useState(false);
const [newGroupId, setNewGroupId] = useState<string | null>(null);

const allGroups = useSelector((state: RootState) => state.groups.items);



    const BackButton = () => (
        <Button
            startIcon={<ArrowBackIcon />}
            variant="outlined"
            onClick={() => {
                navigate(-1);
                TeamService.setTeam(null);
            }}
            sx={{ mr: 2 }}
        >
            Back
        </Button>
    );

    useEffect(() => {
        TagsService.fetchAll(() => { });
    }, []);

    // Load team data if we're in edit mode
    useEffect(() => {
        if (isEditMode && teamId) {
            // Always try to find the team in the store items first to get the latest data
            const foundTeam = allTeams.find(team => team.id === teamId);
            if (foundTeam) {
                // Always use the team from store items (which has the latest data)
                TeamService.setTeam(foundTeam);
            } else {
                // Team not found in store, fetch all teams first
                TeamService.fetchAll((isValid) => {
                    if (isValid) {
                        const updatedTeams = store.getState().teams.items;
                        const team = updatedTeams.find(t => t.id === teamId);
                        if (team) {
                            TeamService.setTeam(team);
                        } else {
                            toast.error("Team not found");
                            navigate('/groups-and-teams');
                        }
                    } else {
                        toast.error("Failed to load team");
                        navigate('/groups-and-teams');
                    }
                });
            }
        } else {
            // Clear selected team for create mode
            TeamService.setTeam(null);
        }
    }, [teamId, isEditMode, allTeams, navigate]);

    const handleDelete = async () => {
        if (!selectedTeam || !isEditMode) return;
        setConfirmOpen(false);
        await TeamService.deleteTeam(selectedTeam.id!, (isValid) => {
            if (isValid) {
                toast.success("Team deleted successfully");
                TeamService.setTeam(null);
                navigate('/groups-and-teams');
            } else {
                toast.error("Failed to delete team");
            }
        });
    };

    const getTitle = () => {
        if (isEditMode) {
            return permissionCheck(customerPermissionFlags.TEAM_LEADER) ? "Edit Team" : "View Team";
        }
        return "Create Team";
    };

    return (
        <Page title={getTitle()}>
            <Container maxWidth={false}>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Stack
                        direction="row"
                        display="flex"
                        alignContent="center"
                        alignItems="center"
                        width="100%"
                    >
                        <BackButton />
                        <h3>{getTitle()}</h3>
                        <Box flexGrow={1} />
                        {
                           isEditMode && selectedTeam && 
                            
                                <Button
                                    variant="outlined"
                                    color="secondary"
                                    onClick={() => setGroupModalOpen(true)}
                                    sx={{
                                        marginRight: 5
                                    }}
                                >
                                    Move Team
                                </Button>
             
                        }

                        {isEditMode && selectedTeam && (
                            <Button
                                disabled={!permissionCheck(customerPermissionFlags.TEAM_LEADER)}
                                variant="contained"
                                color="error"
                                onClick={() => setConfirmOpen(true)}
                            >
                                Delete
                            </Button>
                        )}
                    </Stack>
                </Stack>

                <Card sx={{ mt: 2, height: "100%" }}>
                    <TeamForm
                        isEdit={!!selectedTeam}
                        defaultValues={{
                            id: selectedTeam?.id ?? "",
                            name: selectedTeam?.name!,
                            location: selectedTeam?.location!,
                            membersIds: selectedTeam?.membersIds,
                            leadersIds: selectedTeam?.leadersIds,
                            tags: selectedTeam?.tags ?? [],
                            settings: selectedTeam?.settings ?? {
                                checkOnMeEnabled: selectedGroup?.settings.checkOnMeEnabled ?? false,
                                calendarEnabled: false,
                                defaultContact: false,
                                contacts: [{ phone_number: "+1", full_name: '' }, { phone_number: "+1", full_name: '' }]
                            }
                        }}
                    />
                </Card>

                {/* Confirm Delete Dialog */}
                <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
                    <DialogTitle>Confirm Delete</DialogTitle>
                    <DialogContent>
                        <DialogContentText>
                            Are you sure you want to delete this team? This action cannot be undone.
                        </DialogContentText>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
                        <Button onClick={handleDelete} color="error" variant="contained">
                            Delete
                        </Button>
                    </DialogActions>
                </Dialog>
                <Dialog open={groupModalOpen} onClose={() => setGroupModalOpen(false)} fullWidth>
                <DialogTitle>Move Team</DialogTitle>
                <DialogContent>
                    <Typography mb={2}>
                    You are about to move this team to a different group. This may affect permissions, visibility, and how users are associated with the group. Are you sure?
                    </Typography>

                    <Select
                    fullWidth
                    value={newGroupId ?? ''}
                    onChange={(e) => setNewGroupId(e.target.value)}
                    displayEmpty
                    >
                    <MenuItem disabled value="">
                        Select a new group
                    </MenuItem>
                    {allGroups.map((group) => (
                        <MenuItem
                        key={group.id}
                        value={group.id}
                        disabled={group.id === selectedTeam?.groupId}
                        >
                        {group.name}
                        </MenuItem>
                    ))}
                    </Select>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setGroupModalOpen(false)}>Cancel</Button>
                    <Button
                    variant="contained"
                    disabled={!newGroupId}
                    onClick={async () => {
                        if (selectedTeam && newGroupId && newGroupId !== selectedTeam.groupId) {
                        await TeamService.changeTeamGroup(selectedTeam.id, newGroupId, (isValid, msg) => {
                            if (isValid) {
                                toast.success('Team moved successfully!');
                                setGroupModalOpen(false);
                                navigate(-1);
                            } else {
                                toast.error(msg ?? 'Failed to move team');
                            }
                        });
                        }
                    }}
                    >
                    Confirm Move
                    </Button>
                </DialogActions>
            </Dialog>
            </Container>
        </Page>
    );
};
